import { lazy } from "react";

// sessionStorage flag so a forced reload happens at most once per session,
// preventing an infinite reload loop if the chunk is genuinely broken.
const REFRESH_KEY = "lazy-chunk-refreshed";

const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = error.name || "";
  const message = error.message || "";
  return (
    name === "ChunkLoadError" ||
    /Loading( CSS)? chunk .* failed/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /failed to fetch dynamically imported module/i.test(message)
  );
};

// Wraps a dynamic import so a failed chunk load doesn't crash the app with the
// "Loading chunk … failed / ChunkLoadError" screen.
//   1. Transient failures (timeouts, flaky network) are retried a few times.
//   2. If it still fails — almost always because a new deploy replaced the old
//      chunk hashes — the page is reloaded once to pull the fresh manifest.
const lazyRetry = (importFn, retries = 2, interval = 600) =>
  lazy(
    () =>
      new Promise((resolve, reject) => {
        const load = (remaining) => {
          importFn()
            .then((module) => {
              // Success — clear the reload guard for future navigations.
              try {
                window.sessionStorage.removeItem(REFRESH_KEY);
              } catch (e) {
                /* ignore storage errors */
              }
              resolve(module);
            })
            .catch((error) => {
              if (remaining > 0) {
                setTimeout(() => load(remaining - 1), interval);
                return;
              }
              if (isChunkLoadError(error)) {
                let alreadyRefreshed = false;
                try {
                  alreadyRefreshed =
                    window.sessionStorage.getItem(REFRESH_KEY) === "true";
                } catch (e) {
                  /* ignore storage errors */
                }
                if (!alreadyRefreshed) {
                  try {
                    window.sessionStorage.setItem(REFRESH_KEY, "true");
                  } catch (e) {
                    /* ignore storage errors */
                  }
                  window.location.reload();
                  return;
                }
              }
              reject(error);
            });
        };
        load(retries);
      })
  );

export default lazyRetry;
export { isChunkLoadError };
